/**
 * Every page of the site, ONE list — the classic page bar (TopNav) and the
 * Page Selector panel both read it, so a page and who may see it are defined
 * once. Visibility mirrors the routes' security-group gates.
 *
 * Groups (Chris, 2026-10-28): the Page Selector shows the pages in these
 * sub-panels, in this order; a user may pin one group to the header and mark
 * one page "1st" (the page the site opens on). Group keys are stored in
 * app.UserNavPrefs (db/123) and validated by api/src/functions/navPrefs.ts —
 * keep the three in step.
 */

export interface NavMe {
  isAdmin: boolean
  isPower: boolean
  isBudget: boolean
  isEngagement: boolean
  isEvents: boolean
  isExec: boolean
  isCustomerService: boolean
}

/** Pill colour family — the existing .dashboard-pill modifiers. */
export type NavStyle = 'plain' | 'budget' | 'claude' | 'my-dashboard' | 'admin'

export interface NavPage {
  to: string
  label: string
  style: NavStyle
  visible: (me: NavMe) => boolean
}

const all = () => true

/** In classic page-bar order (Admin sits apart at the right of that bar). */
export const NAV_PAGES: readonly NavPage[] = [
  // Dashboard One points at its OWN route, not '/'. '/' redirects to the
  // user's landing page, which used to make this pill un-clickable.
  { to: '/dashboard-one', label: 'Dashboard One', style: 'plain', visible: all },
  { to: '/dashboard-two', label: 'Groups', style: 'plain', visible: all },
  { to: '/pullman', label: 'Pullman Reports', style: 'plain', visible: all },
  { to: '/bookings', label: 'Pullman Bookings', style: 'plain', visible: all },
  { to: '/pullman-demand', label: 'Pullman Demand', style: 'plain', visible: all },
  { to: '/pullman-live', label: 'Pullman Live', style: 'plain', visible: all },
  { to: '/sales', label: 'Retail & Tea Rooms', style: 'plain', visible: all },
  { to: '/booking-offices', label: 'Booking Offices', style: 'plain', visible: all },
  { to: '/customer-service', label: 'Customer Service', style: 'plain', visible: (m) => m.isCustomerService },
  { to: '/budget', label: 'Budget', style: 'budget', visible: (m) => m.isBudget },
  { to: '/gift-aid', label: 'Gift Aid', style: 'budget', visible: (m) => m.isBudget },
  { to: '/engagement', label: 'Engagement', style: 'budget', visible: (m) => m.isEngagement },
  { to: '/fundraising', label: 'Fundraising', style: 'budget', visible: (m) => m.isEngagement },
  { to: '/foundation', label: 'Foundation', style: 'budget', visible: (m) => m.isEngagement || m.isAdmin },
  { to: '/membership', label: 'Membership', style: 'budget', visible: (m) => m.isEngagement || m.isAdmin },
  { to: '/events-revenue', label: 'Events', style: 'budget', visible: (m) => m.isEvents },
  { to: '/research', label: 'Research', style: 'budget', visible: (m) => m.isPower },
  { to: '/timetable-studio', label: 'Timetable Studio', style: 'budget', visible: (m) => m.isExec || m.isAdmin },
  { to: '/executive', label: 'Executive', style: 'budget', visible: (m) => m.isExec },
  { to: '/claude-help', label: 'Claude Help', style: 'claude', visible: (m) => m.isAdmin || m.isPower || m.isBudget },
  // Always last in the row: it used to jump between the start and the end of
  // the row depending on whether the user had pins.
  { to: '/my-dashboard', label: 'My Dashboard', style: 'my-dashboard', visible: all },
  { to: '/admin', label: 'Admin', style: 'admin', visible: (m) => m.isAdmin },
]

export type NavGroupKey = 'general' | 'pullman' | 'engagement' | 'executive' | 'commercial' | 'misc'

export interface NavGroup {
  key: NavGroupKey
  title: string
  pages: readonly string[]
}

export const NAV_GROUPS: readonly NavGroup[] = [
  { key: 'general', title: 'General Reports', pages: ['/dashboard-one', '/dashboard-two', '/gift-aid'] },
  { key: 'pullman', title: 'Pullman', pages: ['/pullman', '/bookings', '/pullman-demand', '/pullman-live'] },
  { key: 'engagement', title: 'Engagement', pages: ['/engagement', '/fundraising', '/foundation', '/membership'] },
  { key: 'executive', title: 'Executive', pages: ['/budget', '/executive', '/research', '/timetable-studio'] },
  { key: 'commercial', title: 'Commercial', pages: ['/events-revenue', '/sales', '/booking-offices', '/customer-service'] },
  { key: 'misc', title: 'Miscellaneous', pages: ['/my-dashboard', '/claude-help', '/admin'] },
]

export const pageByPath = (to: string): NavPage | undefined => NAV_PAGES.find((p) => p.to === to)

/** A group's pages this user may see, in the group's order. */
export function visibleGroupPages(g: NavGroup, me: NavMe): NavPage[] {
  return g.pages.map(pageByPath).filter((p): p is NavPage => !!p && p.visible(me))
}

/** Does `pathname` sit on page `to`? ('/pullman' must not claim '/pullman-demand'.) */
export const onPage = (pathname: string, to: string): boolean =>
  pathname === to || pathname.startsWith(to + '/')
